import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import { accountingAPI, ledgerAPI } from '../../services/api';
import { useAppSnackbar } from '../common/TableDesign';
import ModulePageHeader from '../common/ModulePageHeader';
import ConfigSection from './ConfigSection';
import { configDescriptors } from './configSections';

const TABS = [
  { key: 'defaults', label: 'Defaults' },
  { key: 'numbering', label: 'Numbering', descriptor: 'numbering' },
  { key: 'paymentMethods', label: 'Payment Methods', descriptor: 'paymentMethods' },
  { key: 'postingRules', label: 'Posting Rules', descriptor: 'postingRules' },
  { key: 'journalTemplates', label: 'Journal Templates', descriptor: 'journalTemplates' },
  { key: 'currencies', label: 'Currencies', descriptor: 'currencies' },
  { key: 'exchangeRates', label: 'Exchange Rates', descriptor: 'exchangeRates' },
  { key: 'tax', label: 'Tax' },
  { key: 'org', label: 'Branches & Depts' },
  { key: 'dimensions', label: 'Dimensions' },
  { key: 'policies', label: 'Policies', descriptor: 'accountingPolicies' },
  { key: 'fiscal', label: 'Fiscal Policy' },
];

const TAX_TABS = ['taxTypes', 'taxRates', 'taxCodes', 'taxExemptions'];
const ORG_TABS = ['branches', 'departments', 'locations', 'costCentres'];
const DIMENSION_TABS = ['dimensions', 'dimensionValues'];

const getErrorMessage = (error, fallback = 'Operation failed') => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  // Surface the ledger's per-field validation detail (forwarded by the
  // Laravel gateway in `errors`) instead of a bare "Validation failed".
  const details = data?.errors;
  if (!details) return message;
  const flat = Array.isArray(details)
    ? details.map((d) => (typeof d === 'string' ? d : JSON.stringify(d)))
    : Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
  return flat.length ? `${message}: ${flat.join('; ')}` : message;
};

// Default accounting settings for this school (read-only): the resolved
// fee -> ledger bridge defaults - which ledger bank account fee payments
// post into, which revenue account allocations bill, and the local
// cashbook -> ledger account map. Values come from the school's tenant
// block when set, otherwise the server .env defaults.
const BridgeDefaultsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    accountingAPI
      .getBridge()
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load default accounting settings.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box py={5} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const ledgerLabel = (ref) => {
    if (!ref) return 'Not set';
    const { account_code: code, account_name: name, account_id: id } = ref;
    return [code, name].filter(Boolean).join(' - ') || `Ledger account #${id} (ledger offline - name unavailable)`;
  };

  return (
    <Box>
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Chip
          label={data.enabled ? 'Bridge enabled' : 'Bridge disabled'}
          color={data.enabled ? 'success' : 'default'}
          size="small"
        />
        <Chip
          label={data.ledger_reachable ? 'Ledger reachable' : 'Ledger unreachable'}
          color={data.ledger_reachable ? 'success' : 'warning'}
          size="small"
          variant="outlined"
        />
        {Array.isArray(data.school_overrides) &&
          data.school_overrides.map((k) => (
            <Chip key={k} label={`${k} (this school)`} size="small" variant="outlined" />
          ))}
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Default bank account
          </Typography>
          <Typography variant="body2">
            {data.bank_account_id
              ? `${ledgerLabel(data.bank_account)} (#${data.bank_account_id})`
              : 'Not set'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Fee payments post here when Record Payment is left on &ldquo;Default ledger bank
            account (automatic)&rdquo;.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Default revenue account
          </Typography>
          <Typography variant="body2">
            {data.revenue_account_id
              ? `${ledgerLabel(data.revenue_account)} (#${data.revenue_account_id})`
              : 'Not set'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Fee allocations bill here - one ledger invoice per student x term x fee group unless a fee type override applies.
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Fee type revenue accounts
      </Typography>
      {Array.isArray(data.revenue_account_map) && data.revenue_account_map.length > 0 ? (
        <List dense>
          {data.revenue_account_map.map((row) => (
            <ListItem key={`${row.fee_type}-${row.ledger_account_id}`} disableGutters>
              <ListItemText
                primary={`Fee type ${row.fee_type}`}
                secondary={`posts into ${ledgerLabel(row.ledger_account)} (#${row.ledger_account_id})`}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No fee type overrides. Allocations use the default revenue account above.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block">
        Fee type mappings are configured by the platform admin for each school.
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Local cashbook &rarr; ledger map
      </Typography>
      {Array.isArray(data.account_map) && data.account_map.length > 0 ? (
        <List dense>
          {data.account_map.map((row) => (
            <ListItem key={row.local_account_id} disableGutters>
              <ListItemText
                primary={`${row.local_account_name || `Local account #${row.local_account_id}`}${
                  row.local_account_type ? ` (${row.local_account_type})` : ''
                }`}
                secondary={`posts into ${ledgerLabel(row.ledger_account)} (#${row.ledger_account_id})`}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No per-account overrides - every local collection account posts into the default bank
          account above.
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Ledger bank / cash accounts ({Array.isArray(data.ledger_bank_accounts) ? data.ledger_bank_accounts.length : 0})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {Array.isArray(data.ledger_bank_accounts) && data.ledger_bank_accounts.length > 0
          ? data.ledger_bank_accounts
              .map((a) => `${a.account_code} - ${a.account_name}${a.payment_method_type ? ` (${a.payment_method_type})` : ''}`)
              .join('  ·  ')
          : 'None linked - open Cash & Banking → Reconciliation accounts and link the GL bank/cash accounts. Fee payments pick from this list.'}
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Local collection accounts ({Array.isArray(data.local_accounts) ? data.local_accounts.length : 0})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {Array.isArray(data.local_accounts) && data.local_accounts.length > 0
          ? data.local_accounts
              .map((a) => `${a.name}${a.account_type ? ` (${a.account_type})` : ''}`)
              .join('  ·  ')
          : 'None yet - create one under Accounting → Accounts for local cashbook tracking. Fee collection still links to the ledger automatically via the default bank account.'}
      </Typography>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Read-only: managed on the server (.env) or the platform-admin school screen. Keys marked
        &ldquo;this school&rdquo; are overridden for this school.
      </Typography>
    </Box>
  );
};

const FiscalPolicyTab = () => {
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  // Backend FiscalPolicy shape (fiscal-policy.service.ts):
  // { max_adjustment_periods_per_year: 1-4, enforce_period_close_checklist: bool,
  //   close_maker_checker: bool, close_checklist_items: [{key,label,required}] }
  const [values, setValues] = useState({
    max_adjustment_periods_per_year: 1,
    enforce_period_close_checklist: false,
    close_maker_checker: false,
  });
  const [checklistItems, setChecklistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    ledgerAPI
      .getFiscalPolicy()
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? {};
        setValues({
          max_adjustment_periods_per_year: Number(
            data.max_adjustment_periods_per_year ?? 1
          ),
          enforce_period_close_checklist: Boolean(data.enforce_period_close_checklist),
          close_maker_checker: Boolean(data.close_maker_checker),
        });
        setChecklistItems(Array.isArray(data.close_checklist_items) ? data.close_checklist_items : []);
      })
      .catch((error) => showSnackbar(getErrorMessage(error, 'Failed to fetch fiscal policy'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    const max = Number(values.max_adjustment_periods_per_year);
    if (!Number.isInteger(max) || max < 1 || max > 4) {
      showSnackbar('Max adjustment periods must be a whole number between 1 and 4', 'error');
      return;
    }
    try {
      setSaving(true);
      await ledgerAPI.updateFiscalPolicy({
        max_adjustment_periods_per_year: max,
        enforce_period_close_checklist: Boolean(values.enforce_period_close_checklist),
        close_maker_checker: Boolean(values.close_maker_checker),
      });
      showSnackbar('Fiscal policy updated successfully', 'success');
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box py={5} textAlign="center">
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Max adjustment periods per year (1-4)"
            value={values.max_adjustment_periods_per_year}
            inputProps={{ min: 1, max: 4, step: 1 }}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, max_adjustment_periods_per_year: e.target.value }))
            }
            helperText="Only adjustment periods can be created manually; regular periods auto-generate."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(values.enforce_period_close_checklist)}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, enforce_period_close_checklist: e.target.checked }))
                }
              />
            }
            label="Enforce period-close checklist"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            When on, a period cannot close until every required checklist item is completed.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(values.close_maker_checker)}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, close_maker_checker: e.target.checked }))
                }
              />
            }
            label="Close maker-checker (SoD)"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            When on, the closer cannot reopen their own period and the locker cannot unlock their
            own period.
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Required close checklist
      </Typography>
      {checklistItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No checklist items configured on the ledger.
        </Typography>
      ) : (
        <List dense>
          {checklistItems.map((item) => (
            <ListItem key={item.key} disableGutters>
              <ListItemText
                primary={`${item.label}${item.required ? ' (required)' : ''}`}
                secondary={item.key}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Box mt={3}>
        <Button variant="contained" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving...' : 'Save Fiscal Policy'}
        </Button>
      </Box>
      {SnackbarComponent}
    </Box>
  );
};

const SubTabbedSection = ({ keys }) => {
  const [sub, setSub] = useState(keys[0]);
  return (
    <Box>
      <Tabs
        value={sub}
        onChange={(_, value) => setSub(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {keys.map((key) => (
          <Tab key={key} value={key} label={configDescriptors[key].title} />
        ))}
      </Tabs>
      <ConfigSection key={sub} descriptor={configDescriptors[sub]} />
    </Box>
  );
};

const AccountingSettings = () => {
  const { can } = useAccessControl();
  const [tab, setTab] = useState('defaults');

  if (!can(RESOURCES.LEDGER.SETTINGS, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view accounting settings.</Alert>;
  }

  return (
    <Box>
      <ModulePageHeader
        title="Accounting Settings"
        subtitle="Default bridge accounts, numbering, payment methods, posting rules, currencies, tax, organisation structure and policies"
      />
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '8px !important', pt: '4px !important' }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={t.label} />
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {tab === 'defaults' && <BridgeDefaultsTab />}
          {tab === 'tax' && <SubTabbedSection keys={TAX_TABS} />}
          {tab === 'org' && <SubTabbedSection keys={ORG_TABS} />}
          {tab === 'dimensions' && <SubTabbedSection keys={DIMENSION_TABS} />}
          {tab === 'fiscal' && <FiscalPolicyTab />}
          {(() => {
            const active = TABS.find((t) => t.key === tab);
            return active?.descriptor ? (
              <ConfigSection key={tab} descriptor={configDescriptors[active.descriptor]} />
            ) : null;
          })()}
          {tab === 'policies' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Tip: use Fiscal Policy for the singleton fiscal-year policy; accounting policies below are the versioned policy register.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AccountingSettings;
